<template>
  <div :class="['delivery-details', context.toLowerCase()]">
    <h1>{{ data.id ? 'Lieferschein' : 'Lieferschein anlegen' }}</h1>
    <hr>

    <div class="grid">
      <delivery-block-seller style="grid-area: seller" v-model="data" :context="context" :disabled="disabled" :saving="saving"/>
      <delivery-block-buyer style="grid-area: buyer" v-model="data" :disabled="disabled" :saving="saving"/>

      <delivery-block-quality-program style="grid-area: quality-programs" v-model="data" :disabled="disabled"
                                      :saving="saving"
                                      v-if="context.toLowerCase() === 'pigsty'"/>

      <delivery-block-delivery-address style="grid-area: delivery-address" v-model="data" :disabled="disabled"
                                       :saving="saving"
                                       v-if="context.toLowerCase() === 'piglet'"/>

      <delivery-block-slaughter-house style="grid-area: slaughter-house" v-model="data" :disabled="disabled"
                                      :saving="saving"
                                      v-if="context.toLowerCase() === 'pigsty'"/>

      <delivery-block-transporter style="grid-area: transporter" v-model="data" :context="context" :disabled="disabled"
                                  :saving="saving"/>
      <delivery-block-transport-details style="grid-area: transport-details" v-model="data" :context="context"
                                        :disabled="disabled" :saving="saving"/>
      <delivery-block-transporter-details style="grid-area: transporter-details" v-model="data" :context="context"
                                          :disabled="disabled" :saving="saving"/>

      <div style="grid-area: note">
        <header>
          <h3>Notiz</h3>
        </header>
        <b-textarea v-model="data.note.comment" :disabled="disabled || saving"/>
      </div>

      <div style="grid-area: positions;">
        <header>
          <h3>Positionen</h3>
        </header>
        <report-item-list :disabled="disabled || saving"
                          :articles="data.articles"
                          :additions="data.additions"
                          :seller="data.sellerFactory"
                          :context="context"
                          @update="onArticlesUpdate"/>
      </div>

      <div style="grid-area: info">
        <header>
          <h3>Information</h3>
        </header>
        <b-row>
          <b-col cols="4" v-if="context.toLowerCase() === 'pigsty'">
            <b-form-group label="Retourschweine" label-cols="6" label-cols-md="6">
              <b-input type="number" v-model="data.returnPigs" :disabled="disabled || saving"/>
            </b-form-group>
          </b-col>
          <b-col cols="4" v-if="context.toLowerCase() === 'piglet'">
            <b-form-group label="Basispreis" label-cols="8" label-cols-md="8">
              <b-input type="number" v-model="data.basePrice" :disabled="disabled || saving"/>
            </b-form-group>
            <b-form-group label="Basispreis:+/- Zu-Abschläge" label-cols="8" label-cols-md="8">
              <b-input type="number" v-model="data.addition" :disabled="disabled || saving"/>
            </b-form-group>
          </b-col>
          <b-col cols="8">
            <b-form-group label="Sonstiges" label-cols="2" label-cols-md="2">
              <b-input type="text" v-model="data.others" :disabled="disabled || saving"/>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div style="grid-area: photos">
        <header>
          <h3>Fotos</h3>
        </header>

        <ul class="images">
          <li v-for="image of data.note.images" :key="image.name">
            <div class="image-wrap">
              <img :src="`data:${typeof image.mime !== 'undefined' ? image.mime : 'image/png'};base64,${image.source}`"
                   v-if="image.source">
            </div>
            <div class="controls">
              <input type="text" class="form-control" placeholder="Beschreibung" v-model="image.description"/>
              <div class="right">
                <b-button variant="danger" size="sm" class="mt-2" @click="removeImage(image)">
                  <font-awesome-icon icon="times"/>
                  Entfernen
                </b-button>
              </div>
            </div>
          </li>
        </ul>

        <input type="file" ref="inputImageUpload" accept="image/*" @change="onFilesSelect" style="display: none"
               multiple/>

        <div class="right">
          <b-button variant="primary" size="sm" class="mt-3" @click="openFileChooser" :disabled="disabled || saving">
            <font-awesome-icon icon="image"/>
            Foto(s) anhängen
          </b-button>
        </div>
      </div>

      <delivery-terms style="grid-area: agb"
                      @valid="onTermsChange"></delivery-terms>

      <div style="grid-area: signatures">
        <header>
          <h3>Unterschriften</h3>
        </header>

        <b-row class="signatures">
          <b-col cols="12" md="4" v-for="config of signatureConfig" :key="config.id">
            <h3>{{ config.label }}</h3>
            <img v-if="data[config.id]" class="signature" :src="data[config.id]"/>
            <div class="right">
              <signature-modal :id="config.id" @ok="dataUrl => data[config.id] = dataUrl"/>
            </div>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-row>
      <b-col cols="12" class="mt-1">
        <slot name="buttons"></slot>
        <b-button variant="success" class="float-right" :disabled="saving || !termsValid" @click="print">
          <font-awesome-icon icon="file-pdf" v-if="!saving"/>
          <font-awesome-icon icon="spinner" spin v-else/>
          PDF
        </b-button>
        <b-button variant="primary" class="mr-1 float-right" :disabled="saving || !termsValid" @click="save">
          <font-awesome-icon icon="check" v-if="!saving"/>
          <font-awesome-icon icon="spinner" spin v-else/>
          Speichern
        </b-button>
        <br style="clear: both">
      </b-col>
    </b-row>

    <modal-select-factory :title="factorySelectorConfig.title"
                          @cancel="() => factorySelectorConfig = { visible: false, title: '' }"
                          @select="factorySelectorConfig.onSelect"
                          v-if="factorySelectorConfig.visible"/>
  </div>
</template>

<script>

import SelectTransport from "@/components/select-transport/SelectTransport.vue";
import SelectPigSty from "@/components/select-pig-sty/select-pig-sty.vue";
import ReportItemList from "@/app/components/report/ReportItemList.vue";
import SignatureModal from "@/app/components/general/SignatureModal.vue";
import {computed, ref, watch} from "@vue/composition-api";
import OfflineIndicator from "@/app/components/OfflineIndicator.vue";
import Activity from "@/app/components/Activity.vue";
import DeliveryNoteApi from '@/api/delivery-note/delivery-note.js'
import ModalSelectFactory from "@/app/components/delivery/ModalSelectFactory.vue";
import AppSelect from "@/app/components/general/AppSelect.vue";
import DeliveryBlockSeller from "@/app/components/delivery/blocks/DeliveryBlockSeller.vue";
import DeliveryBlockBuyer from "@/app/components/delivery/blocks/DeliveryBlockBuyer.vue";
import DeliveryBlockDeliveryAddress from "@/app/components/delivery/blocks/DeliveryBlockDeliveryAddress.vue";
import DeliveryBlockTransporter from "@/app/components/delivery/blocks/DeliveryBlockTransporter.vue";
import DeliveryBlockTransportDetails from "@/app/components/delivery/blocks/DeliveryBlockTransportDetails.vue";
import DeliveryBlockTransporterDetails from "@/app/components/delivery/blocks/DeliveryBlockTransporterDetails.vue";
import DeliveryBlockSlaughterHouse from "@/app/components/delivery/blocks/DeliveryBlockSlaughterHouse.vue";
import DeliveryBlockQualityProgram from "@/app/components/delivery/blocks/DeliveryBlockQualityProgram.vue";
import DeliveryTerms from "@/app/components/delivery/blocks/DeliveryTerms.vue";

const defaultData = {
  context: null, // piglet, pigSty
  basePrice: null, // default aus Stammdaten-PigPrice
  sellerFactoryId: null, // Verkäufer GUID aus Stammdaten-Factory
  sellerFactory: {
    lfbis: null,
    ringNr: null,
    contact: null,
    phone: null,
    veterinaryTgd: 'Ja',
    veterinary: null,
  },
  buyerFactoryId: "869bb6e7-db67-4e81-96f0-e0e4f58bd947", // Käufer GUID aus Stammdaten-Factory
  buyerFactory: {
    lfbis: '10001217',
    contact: "STYRIABRID I\n" +
      "Schulstraße 14\n" +
      "8423 St. Veit a. V.\n" +
      "Österreich",
  },
  deliveryFactoryId: "869bb6e7-db67-4e81-96f0-e0e4f58bd947", // Lieferaddresse GUID aus Stammdaten-Factory
  deliveryFactory: {
    lfbis: '10001217',
    ringNr: 'Q 01',
    contact: "STYRIABRID I\n" +
      "Schulstraße 14\n" +
      "8423 St. Veit a. V.\n" +
      "Österreich",
  },
  transferTransportId: null, // Übernahmestelle INT aus Stammdaten-Transport
  transferTransport: null,
  loadingTransportId: null, // Verladeort INT aus Stammdaten-Transport
  loadingTransport: null,
  lastFeeding: null, // Letzte Fütterung DateTime-Datepicker
  cleaned: null, // Gereinigt DateTime-Datepicker
  transportId: null, // Transporteur INT aus Stammdaten-Transport
  transport: null,
  transporterId: null, // LKW INT aus Stammdaten-Transporter
  secondTransporterId: null, // LKW 2 INT aus Stammdaten-Transporter
  transporter: null, // LKW Kennzeichen
  secondTransporter: null, // LKW 2 Kennzeichen
  slaughterHouseId: null,
  slaughterHouse: null,
  qualityProgramIds: [],
  qualityProgram: null,
  dischargeTransportId: null, // Entladeort INT aus Stammdaten-Tranport
  dischargeTransport: null,
  transportStart: null, // NEU!
  carriageDuration: null, // Beförderungszeit TimeSpan
  articles: [
    // {
    //   "pieces": 100, // Stück int
    //   "articleId": 1, // Kategorie int aus Stammdaten-Article
    //   "nativityId": "GUID", // Herkunft GUID aus Stammdaten-Factory
    //   "weight": 1147.00, // Gewicht INT
    //   "notice": "xxx" // Nähere Angaben STRING
    // }
  ],
  additions: [], // Zu- / Abschläge INT Ids aus Stammdaten-Additions
  signatureBuyer: null, //Unterschrift des Käufer Datatype TBD
  signatureSeller: null, // Unterschrift Verkäufer Datatype TBD
  signatureTransporter: null, // Unterschrift Transporteur  Datatype TBD
  note: {
    comment: "", //Kommentar
    images: []
  }
}

export default {
  components: {
    DeliveryBlockQualityProgram,
    DeliveryBlockSlaughterHouse,
    DeliveryBlockTransporterDetails,
    DeliveryBlockTransportDetails,
    DeliveryBlockTransporter,
    DeliveryBlockDeliveryAddress,
    DeliveryBlockBuyer,
    DeliveryTerms,
    DeliveryBlockSeller,
    AppSelect,
    ModalSelectFactory,
    ReportItemList, SignatureModal, SelectTransport, SelectPigSty, OfflineIndicator, Activity
  },

  props: {
    context: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    delivery: {
      type: Object,
      default: () => ({}),
    }
  },

  setup({context, delivery}) {
    const store = window.useStore()
    const router = window.useRouter()
    const {tc} = window.useI18n()

    const inputImageUpload = ref(null)
    const termsValid = ref(false);

    const factorySelectorConfig = ref({
      visible: false,
      title: '',
    })

    const saving = ref(false)

    const data = ref({
      ...defaultData,
      context,
      ...delivery,
    })

    const signatureConfig = [
      {id: 'signatureSeller', label: 'Verkäufer'},
      {id: 'signatureTransporter', label: 'Transporteur'},
      {id: 'signatureBuyer', label: 'Käufer'},
    ]

    watch(() => data.value.transportStart, () => {
      if (data.value.lastFeeding) {
        return null
      }
      const date = new Date(Date.parse(data.value.transportStart))
      date.setMinutes(date.getMinutes() - 15)
      data.value.lastFeeding = [
        [
          date.getFullYear(),
          ('0' + (date.getMonth() + 1)).substr(-2),
          ('0' + date.getDate()).substr(-2)
        ].join('-'),
        [
          ('0' + date.getHours()).substr(-2),
          ('0' + date.getMinutes()).substr(-2),
        ].join(':')
      ].join('T')
    })

    const loaded = computed(() => store.state.pwa.pigSty.lastFetched !== null)
    const loading = computed(() => store.getters['backgroundDataLoading'])

    if (!loaded && !loading.value) {
      store.dispatch('APP_FETCH_BACKGROUND_DATA')
    }

    const reload = () => store.dispatch('APP_FETCH_BACKGROUND_DATA')

    const openFileChooser = () => inputImageUpload.value ? inputImageUpload.value.click() : null

    const onFilesSelect = (e) => {
      Array.from(e.target.files).forEach(async (f) => {
        const source = await new Promise(resolve => {
          const reader = new FileReader()
          reader.onloadend = res => {
            resolve(btoa(res.target.result))
          }
          reader.readAsBinaryString(f)
        })
        data.value.note.images = [
          ...data.value.note.images,
          {
            name: f.name,
            mime: f.type,
            source,
            description: null,
          }
        ]
      })
    }

    const onArticlesUpdate = ({articles, additions}) => {
      data.value.articles = articles
      data.value.additions = additions
    }

    const removeImage = (image) => {
      data.value.note.images = data.value.note.images.filter(i => i !== image)
    }

    const producers = computed(() => {
      if (!store.state.pwa.pigSty.data.length) {
        return []
      }
      return store.state.pwa.pigSty.data.reduce((res, item) => {
        res.push(item)
        return res
      }, [])
    })

    const transports = computed(() => {
      return store.state.pwa.deliveries.piglet.config.transport.data
    })

    const onTermsChange = valid => {
      termsValid.value = valid;
    }


    const save = async () => {
      saving.value = true
      try {
        const res = await DeliveryNoteApi.addOrUpdate(data.value)
        const id = res.data

        store.commit('APP_DELIVERY_NOTE_LOADED', {id, data: data.value})
        store.dispatch('NOTIFY_MSG', 'app.delivery.notify.saved', {root: true});

        if (typeof data.value.id === 'undefined') {
          router.push({name: 'DeliveryActivity', params: {id}})
        }
      } catch (e) {
        console.error(e)
      }
      saving.value = false
    }

    const print = async() => {
      await save();
      router.push({ name: 'DeliveryPDFActivity', params: { id: delivery.id } });
    };

    return {
      tc,
      factorySelectorConfig,
      producers,
      transports,
      reload,
      data,
      inputImageUpload,
      openFileChooser,
      onFilesSelect,
      removeImage,
      signatureConfig,
      onArticlesUpdate,
      onTermsChange,
      termsValid,
      saving,
      save,
      print
    }
  }
}
</script>

<style lang="scss">
h2 {
  margin-bottom: 30px;
}

.activity.report > .container {
  max-width: 1140px !important;
}

.delivery-details {
  & > .grid {
    display: grid;

    & > div {
      margin: 5px;
      padding: 20px;

      &:not(.no-bg) {
        background: #f7f7f7;
      }

      header {
        margin-bottom: 20px;

        h3 {
          float: left;
        }

        button {
          float: right;
        }

        &:after {
          content: '';
          display: block;
          clear: both;
        }
      }
    }
  }

  &.piglet > .grid {
    grid-template-columns: 50% 50%;
    grid-template-areas: " seller            buyer "
                         " seller            delivery-address "
                         " transport-details transporter "
                         " transport-details transporter-details "
                         " note              note "
                         " positions         positions "
                         " info              info "
                         " photos            photos "
                         " agb               agb "
                         " signatures        signatures ";

    @media screen and (max-width: 850px) {
      grid-template-columns: 100%;

      grid-template-areas: " seller "
                           " buyer "
                           " delivery-address "
                           " transporter "
                           " transport-details "
                           " transporter-details "
                           " note "
                           " positions "
                           " info "
                           " photos "
                           " agb "
                           " signatures ";
    }
  }

  &.pigsty > .grid {
    grid-template-columns: 50% 50%;
    grid-template-areas: " seller            buyer "
                         " seller            slaughter-house "
                         " quality-programs  transporter "
                         " transport-details transporter-details "
                         " note              note "
                         " positions         positions "
                         " info              info "
                         " photos            photos "
                         " agb               agb "
                         " signatures        signatures ";

    @media screen and (max-width: 850px) {
      grid-template-columns: 100%;

      grid-template-areas: " seller "
                           " buyer "
                           " quality-programs "
                           " slaughter-house "
                           " transporter "
                           " transport-details "
                           " transporter-details "
                           " note "
                           " positions "
                           " info "
                           " photos "
                           " agb "
                           " signatures ";
    }
  }
}

ul.images {
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }

  li {
    height: 100%;
    padding: 16px 12px 12px;
    background: #f5f5f5;
    text-align: center;
    display: flex;
    flex-direction: column;

    .image-wrap {
      display: flex;
      justify-content: center;
      height: calc(100% - 80px);

      img {
        align-self: center;
      }
    }

    &:not(:last-child) {
      margin-bottom: 10px;
    }

    img {
      max-height: 200px;
      max-width: 100%;
      margin-bottom: 12px;
    }
  }
}

.right {
  text-align: right;
}

img.signature {
  width: 100%;
}

.row.signatures {
  & > div {
    &:not(:last-child) {
      margin-bottom: 20px;
    }

    @media screen and (min-width: 768px) {
      & > * {
        text-align: center;
      }
    }

    button {
      margin-top: 10px;
    }
  }
}
</style>
